import { AppBar, Theme, Stack } from '@mui/material';
import TabPanel from './tab-panel';
import {
  forwardRef,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import { TabPanel as TabPanelType } from './helpers';
import TabBar from './tab-bar';
import useFeature from '@/hooks/use-feature.hook';

interface Props {
  panels: TabPanelType[];
  onChange?: (tabPath: string) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  toggleTabs: {
    '& header': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.darkGray,
      borderColor: theme.palette.darkGray,
      borderRadius: 5,
    },
    '& .MuiTab-root.Mui-selected': {
      borderBottomColor: theme.palette.surface.secondary.main,
    },
  },
}));

export interface ToggleTabsInstance {
  setTabPath: (tabPath: string) => void;
}

const ToggleTabs = forwardRef<ToggleTabsInstance, Props>(({ panels, onChange }, ref) => {
  const styles = useStyles();
  const [tabPath, setTabPath] = useState('');
  const { hasFeature } = useFeature();

  const handleChangeTabPath = useCallback(
    (tabPath: string) => {
      setTabPath(tabPath);
      onChange?.(tabPath);
    },
    [onChange]
  );

  useImperativeHandle(ref, () => ({
    setTabPath: handleChangeTabPath,
  }));

  const validPanels = useMemo(() => {
    return panels.filter(({ feature }) => !feature || hasFeature(feature));
  }, [hasFeature, panels]);

  const validTabPath = useMemo(() => {
    const panel = validPanels.find(({ path }) => path === tabPath);
    return !!panel && !panel.disabled;
  }, [validPanels, tabPath]);

  const tabIndex = useMemo(() => {
    if (!validTabPath) {
      return 0; // Default to the first tab if no match or disabled
    }
    const panelIndex = validPanels.findIndex(({ path }) => path === tabPath);
    return panelIndex > -1 ? panelIndex : 0;
  }, [validTabPath, validPanels, tabPath]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, tabIndex: number) => {
      handleChangeTabPath(validPanels[tabIndex]?.path || '');
    },
    [validPanels, handleChangeTabPath]
  );

  const PanelEl: ReactNode = useMemo(() => {
    if (!validTabPath) {
      return null;
    }
    const panel = validPanels.find(({ path }) => path === tabPath);
    const { component } = panel || {};
    return component || null;
  }, [validTabPath, validPanels, tabPath]);

  useEffect(() => {
    if (!validTabPath) {
      const { path: firstPath } = validPanels[0] || {};
      handleChangeTabPath(firstPath || '');
    }
  }, [validPanels, validTabPath, handleChangeTabPath]);

  return (
    <Stack className={styles.toggleTabs} height="100%">
      <AppBar position="static" elevation={1}>
        <TabBar tabIndex={tabIndex} panels={validPanels} onTabChange={handleChange} />
      </AppBar>
      <TabPanel tabIndex={tabIndex}>{PanelEl}</TabPanel>
    </Stack>
  );
});

ToggleTabs.displayName = 'ToggleTabs';

export default ToggleTabs;
